/**
 * App-store CTA props — the one place a store link is described.
 *
 * The store buttons on this site (5 placements × 2 stores) were each
 * hand-written as the same
 * four attributes (`href`, `target`, `rel`, and now `onClick`), which is
 * four chances per button to add a seventh link that quietly reports
 * nothing. Store click-through is the most valuable number a marketing site
 * produces, and an uninstrumented button looks identical to an instrumented
 * one in review.
 *
 * So the quad lives here and call sites spread it. Adding a store link
 * without an event now requires hand-writing an `href` to a store URL,
 * which is conspicuous in a diff in a way that a missing `onClick` is not.
 *
 * @module components/landing/store-cta
 */

import { APP_STORE_URL, PLAY_STORE_URL } from '@config/store-urls';
import { trackCtaClick } from '@logging';

/** Which store. */
export type StoreTarget = 'ios' | 'android';

/**
 * Where on the page the button sits.
 *
 * A closed union rather than a free string: `placement` is a dashboard
 * GROUP BY, and a typo would silently create a fifth placement that splits
 * one row into two without failing anything.
 */
export type StorePlacement = 'nav' | 'hero' | 'download_section' | 'footer' | 'demos';

const STORES: Readonly<Record<StoreTarget, { url: string; ctaId: string }>> = {
  ios: { url: APP_STORE_URL, ctaId: 'app_store_ios' },
  android: { url: PLAY_STORE_URL, ctaId: 'play_store_android' },
};

interface StoreCtaProps {
  href: string;
  target: '_blank';
  rel: string;
  onClick: () => void;
}

/**
 * Props for one store link, including its click event.
 *
 * Spread onto whichever styled anchor the surface uses:
 *
 * ```tsx
 * <StoreButton as="a" {...storeCtaProps('ios', 'nav')}>…</StoreButton>
 * ```
 *
 * These open in a new tab, so the current page survives the click. The
 * event is still flushed on the unload-safe path by `trackCtaClick` —
 * `target="_blank"` is a property of today's markup, not a guarantee, and
 * the failure it would cause is silent.
 */
export function storeCtaProps(
  store: StoreTarget,
  placement: StorePlacement,
): StoreCtaProps {
  const { url, ctaId } = STORES[store];
  return {
    href: url,
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: () => {
      trackCtaClick(ctaId, placement);
    },
  };
}
